import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { AlertTriangle, Zap, Shield, Target, TriangleAlert, Brain, Gauge } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { LiveCommitSummary } from "./LiveCommitSummary";
import { OnboardingEvidenceBlock } from "./OnboardingEvidenceBlock";

// ─── Types ─────────────────────────────────────────────
export interface OnboardingContext {
  optionA?: { label: string; description: string };
  optionB?: { label: string; description: string };
  worstCase?: string;
  reversibility?: string;
  urgencyScore?: number;
  impactScore?: number;
  dominantDimension?: string;
  previousAttempts?: string;
  decisionScope?: string;
  previousAttemptsData?: {
    checks: string[];
    learning: string;
    signal: string;
    blocker: string;
    blockerType: string;
    validationPercent: number;
  };
  initialPreference?: 'a' | 'b' | 'undecided';
  computedImpactLevel?: string;
}

export interface GeneratedThesis {
  thesis_text: string;
  option_chosen: string;
  option_rejected: string;
  expected_outcome: string;
  timeframe_days: number;
  dominant_risk: string;
  reversibility: string;
  // Structured fields
  option_selected?: string;
  core_assumptions?: string[];
  expected_signals?: string[];
  main_risk?: string;
}

export interface Phase11Artifacts {
  thesis_text: string;
  killer_condition: string;
  confidence?: number;
}

interface Phase11CommitGateProps {
  onValueChange: (value: string) => void;
  onArtifactsChange?: (data: Phase11Artifacts) => void;
  onGateStatusChange?: (isValid: boolean) => void;
  onboardingContext?: OnboardingContext;
  generatedThesis?: GeneratedThesis;
  className?: string;
}

// ─── Validation ────────────────────────────────────────
const SOFT_WORDS = /\b(gefühl|klarheit|besser|gefühlt|irgendwie|vielleicht|eventuell|hoffentlich|mehr klarheit|gutes gefühl)\b/i;
const MEASURABLE_MARKERS = /\b(wenn|falls|sofern|sobald|mindestens|höchstens|>=|<=|>|<|innerhalb|bis zum|spätestens|\d+)/i;

const getReversibilityColor = (rev?: string) => {
  switch (rev) {
    case "leicht": case "fully_reversible": return "bg-primary/10 text-primary border-primary/20";
    case "mittel": case "partially_reversible": return "bg-secondary/10 text-secondary border-secondary/20";
    case "schwer": return "bg-destructive/10 text-destructive border-destructive/20";
    case "irreversibel": case "irreversible": return "bg-destructive/15 text-destructive border-destructive/30";
    default: return "bg-muted/30 text-muted-foreground border-border";
  }
};

const getRevLabel = (rev?: string) => {
  switch (rev) {
    case "fully_reversible": return "leicht";
    case "partially_reversible": return "mittel";
    case "irreversible": return "irreversibel";
    default: return rev || "–";
  }
};

const checkRiskAlignment = (killerCondition: string, dominantRisk?: string): boolean => {
  if (!dominantRisk || !killerCondition || killerCondition.length < 10) return true;
  // Split on whitespace, hyphens, commas, and common German punctuation to handle compound words
  const splitPattern = /[\s\-–—,;:\/\(\)]+/;
  const riskTokens = dominantRisk.toLowerCase().split(splitPattern).filter(w => w.length > 3);
  const condTokens = killerCondition.toLowerCase().split(splitPattern).filter(w => w.length > 3);
  const condText = killerCondition.toLowerCase();
  
  // Check 1: Direct token overlap (any risk word appears in condition text)
  const directMatch = riskTokens.some(w => condText.includes(w));
  if (directMatch) return true;
  
  // Check 2: Partial stem matching (first 5+ chars match to catch German inflections)
  const hasStemMatch = riskTokens.some(rw => {
    const stem = rw.slice(0, Math.max(5, Math.floor(rw.length * 0.6)));
    return condTokens.some(cw => cw.startsWith(stem) || stem.startsWith(cw.slice(0, Math.max(5, Math.floor(cw.length * 0.6)))));
  });
  if (hasStemMatch) return true;
  
  // Check 3: Semantic domain overlap (>= 2 tokens from same semantic field)
  const overlapCount = riskTokens.filter(rw => 
    condTokens.some(cw => rw.includes(cw) || cw.includes(rw))
  ).length;
  return overlapCount >= 2;
};

// ─── Component ─────────────────────────────────────────
export const Phase11CommitGate = ({
  onValueChange,
  onArtifactsChange,
  onGateStatusChange,
  onboardingContext,
  generatedThesis,
  className,
}: Phase11CommitGateProps) => {
  const [killerCondition, setKillerCondition] = useState("");
  const [softWarning, setSoftWarning] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  const thesis = generatedThesis;
  const ctx = onboardingContext;
  const dominantRisk = thesis?.main_risk || thesis?.dominant_risk || ctx?.worstCase;

  // Structured fields
  const assumptions = thesis?.core_assumptions || [];
  const optionSelected = thesis?.option_selected || thesis?.option_chosen;

  // New OS-level fields
  const initialPreference = ctx?.initialPreference;
  const computedImpactLevel = ctx?.computedImpactLevel;
  const previousAttemptsData = ctx?.previousAttemptsData;
  const isHighImpact = computedImpactLevel === 'strategic' || computedImpactLevel === 'existential';
  const impactLabel = computedImpactLevel === 'existential' ? 'Existenzrelevant'
    : computedImpactLevel === 'strategic' ? 'Strategisch'
    : computedImpactLevel === 'tactical' ? 'Taktisch'
    : computedImpactLevel === 'operational' ? 'Operativ' : undefined;

  // Preference label
  const prefLabel = initialPreference === 'a' ? ctx?.optionA?.label || 'Option A'
    : initialPreference === 'b' ? ctx?.optionB?.label || 'Option B'
    : initialPreference === 'undecided' ? 'Unentschieden' : undefined;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (killerCondition.length > 5) {
        setSoftWarning(SOFT_WORDS.test(killerCondition) ? "Das ist kein Kipppunkt. Das ist eine Hoffnung." : null);
      } else {
        setSoftWarning(null);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [killerCondition]);

  useEffect(() => {
    onValueChange(killerCondition.trim() ? `Kipppunkt: ${killerCondition.trim()}` : "");
    if (onArtifactsChange) {
      onArtifactsChange({
        thesis_text: thesis?.thesis_text || "",
        killer_condition: killerCondition.trim(),
      });
    }
  }, [killerCondition]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMeasurable = MEASURABLE_MARKERS.test(killerCondition);
  const isFalsifiable = killerCondition.length >= 10 && !SOFT_WORDS.test(killerCondition);
  const isRiskAligned = checkRiskAlignment(killerCondition, dominantRisk);
  const hasStructuredData = assumptions.length > 0 || !!optionSelected;

  // Escalation: high-impact requires minimum length + timeframe
  const meetsEscalation = !isHighImpact || (killerCondition.length >= 20 && isMeasurable);

  // ─── Hard Gate: all criteria must pass ───
  const gateValid = isFalsifiable && isMeasurable && isRiskAligned && meetsEscalation && killerCondition.length >= 10;

  useEffect(() => {
    onGateStatusChange?.(gateValid);
  }, [gateValid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Bias-Shift Detection ───
  const biasShift = (() => {
    if (!initialPreference || !optionSelected) return null;
    const selectedLower = optionSelected.toLowerCase();
    const optALabel = ctx?.optionA?.label?.toLowerCase() || '';
    const optBLabel = ctx?.optionB?.label?.toLowerCase() || '';
    const selectedIsA = optALabel && selectedLower.includes(optALabel);
    const selectedIsB = optBLabel && selectedLower.includes(optBLabel);
    if (initialPreference === 'a' && selectedIsA) return 'confirmed';
    if (initialPreference === 'b' && selectedIsB) return 'confirmed';
    if (initialPreference === 'a' && selectedIsB) return 'diverged';
    if (initialPreference === 'b' && selectedIsA) return 'diverged';
    if (initialPreference === 'undecided') return 'resolved';
    return null;
  })();

  // ─── Evidence-Contradiction Detection ───
  const evidenceContradiction = (() => {
    if (!previousAttemptsData || !dominantRisk) return null;
    const highValidation = previousAttemptsData.validationPercent >= 60;
    const hasHighRisk = isHighImpact || (dominantRisk && dominantRisk.length > 20);
    if (highValidation && hasHighRisk) return 'high_evidence_high_risk';
    if (previousAttemptsData.validationPercent < 30 && !isHighImpact) return 'low_evidence_low_stakes';
    return null;
  })();

  return (
    <div className={cn("space-y-6", className)}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Left Column */}
        <div className="space-y-5">

          {/* ═══ ARBEITSPOSITION Card ═══ */}
          <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-card shadow-md">
            <div className="h-1.5 bg-gradient-to-r from-primary via-secondary to-primary/60" />
            
            <div className="p-5 md:p-6 space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15 border border-primary/20">
                    <Zap className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wide">
                      Deine Arbeitsposition
                    </h3>
                    <p className="text-[10px] text-muted-foreground tracking-wide">
                      Strategischer Standpunkt auf Basis deiner Analyse
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-widest border-primary/30 text-primary bg-primary/5 px-2.5">
                  Bindend
                </Badge>
              </div>

              {/* Bias + Impact Badges */}
              {(prefLabel || impactLabel) && (
                <div className="flex flex-wrap items-center gap-2">
                  {prefLabel && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/40 border border-border/40">
                      <Brain className="w-3 h-3 text-muted-foreground" />
                      <span className="text-[10px] font-semibold text-muted-foreground">Initiale Tendenz:</span>
                      <span className="text-[10px] font-bold text-foreground">{prefLabel}</span>
                    </div>
                  )}
                  {impactLabel && (
                    <div className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-full border",
                      isHighImpact
                        ? "bg-destructive/5 border-destructive/20"
                        : "bg-muted/40 border-border/40"
                    )}>
                      <Gauge className={cn("w-3 h-3", isHighImpact ? "text-destructive" : "text-muted-foreground")} />
                      <span className="text-[10px] font-semibold text-muted-foreground">Tragweite:</span>
                      <span className={cn("text-[10px] font-bold", isHighImpact ? "text-destructive" : "text-foreground")}>{impactLabel}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ BIAS-SHIFT DETECTION ═══ */}
              {biasShift && (
                <div className={cn(
                  "flex items-start gap-2.5 p-3 rounded-xl border",
                  biasShift === 'diverged'
                    ? "bg-secondary/5 border-secondary/20"
                    : biasShift === 'confirmed'
                    ? "bg-muted/30 border-border/30"
                    : "bg-primary/5 border-primary/20"
                )}>
                  <Brain className={cn("w-4 h-4 mt-0.5 flex-shrink-0",
                    biasShift === 'diverged' ? "text-secondary" : biasShift === 'confirmed' ? "text-muted-foreground" : "text-primary"
                  )} />
                  <div>
                    <p className={cn("text-xs font-bold uppercase tracking-widest mb-0.5",
                      biasShift === 'diverged' ? "text-secondary" : "text-muted-foreground"
                    )}>
                      {biasShift === 'diverged' ? 'Bias-Shift erkannt' : biasShift === 'confirmed' ? 'Confirmation-Risiko' : 'Tendenz aufgelöst'}
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {biasShift === 'diverged'
                        ? `Deine initiale Tendenz war ${prefLabel}, die Analyse empfiehlt eine andere Richtung. Das stärkt die Prozessqualität.`
                        : biasShift === 'confirmed'
                        ? `Analyse und initiale Tendenz (${prefLabel}) stimmen überein. Prüfe bewusst, ob Confirmation Bias eine Rolle spielt.`
                        : `Du warst initial unentschieden – die Analyse hat eine klare Richtung identifiziert.`}
                    </p>
                  </div>
                </div>
              )}

              {/* ═══ STRUCTURED THESIS ═══ */}
              {hasStructuredData ? (
                <div className="space-y-5">
                  {/* Section 1: Position */}
                  <div className="relative pl-4 py-3 bg-primary/[0.04] rounded-xl border border-primary/15">
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-full bg-gradient-to-b from-primary to-primary/40" />
                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1.5">Wir setzen auf</p>
                    <p className="text-lg font-bold text-foreground leading-tight">
                      {optionSelected}
                    </p>
                  </div>

                  {/* Section 2: Annahmen (max 3) */}
                  {assumptions.length > 0 && (
                    <div className="space-y-2.5">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                        Unsere Wette basiert auf
                      </p>
                      <ul className="space-y-2">
                        {assumptions.slice(0, 3).map((a, i) => (
                          <li key={i} className="flex items-start gap-2.5 pl-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2 flex-shrink-0" />
                            <span className="text-sm leading-relaxed text-foreground/85">{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Section 3: Dominantes Risiko */}
                  {dominantRisk && (
                    <div className="flex items-start gap-3 p-3.5 bg-destructive/5 border border-destructive/15 rounded-xl">
                      <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-destructive/10 flex-shrink-0 mt-0.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-destructive uppercase tracking-widest mb-1">Dominantes Risiko</p>
                        <p className="text-sm font-semibold text-foreground/90 leading-relaxed">{dominantRisk}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : thesis?.thesis_text ? (
                /* Fallback: legacy freetext thesis */
                <div className="relative pl-4 py-3 bg-primary/[0.03] rounded-xl border border-primary/10">
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-full bg-gradient-to-b from-primary to-primary/30" />
                  <p className="text-[15px] leading-[1.75] text-foreground font-serif">
                    „{thesis.thesis_text}"
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-muted/20 border border-border/40 rounded-xl">
                  <p className="text-sm text-muted-foreground">
                    Keine These vorhanden. Die These wird beim Start der Session aus deinen Onboarding-Daten generiert.
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-1.5">
                    Falls du gerade erst gestartet hast, lade die Seite neu.
                  </p>
                </div>
              )}

              {/* Reversibility only – no option chip */}
            </div>
          </div>

          {/* ═══ ONBOARDING-EVIDENZ Block ═══ */}
          {previousAttemptsData && (
            <OnboardingEvidenceBlock data={previousAttemptsData} />
          )}

          {/* ═══ EVIDENCE-CONTRADICTION Challenge ═══ */}
          {evidenceContradiction === 'high_evidence_high_risk' && (
            <div className="flex items-start gap-3 p-4 rounded-2xl border border-secondary/25 bg-secondary/5">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary/10 flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-secondary" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-secondary uppercase tracking-widest mb-1">Systemprüfung: Widerspruch erkannt</p>
                <p className="text-sm text-foreground/85 leading-relaxed">
                  Deine Marktevidenz ist hoch ({previousAttemptsData?.validationPercent}%), aber du wählst eine Hochrisiko-These. 
                  Warum widersprichst du deiner eigenen Evidenz? Stelle sicher, dass dein Kipppunkt diesen Widerspruch adressiert.
                </p>
              </div>
            </div>
          )}

          {/* ═══ Falsification Input ═══ */}
          <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm">
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-primary/60 via-primary/20 to-transparent" />
            
            <div className="p-5 md:p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0 mt-0.5 border border-primary/15">
                  <Target className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">
                    Welches Ereignis würde beweisen, dass diese Entscheidung falsch ist?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Definiere ein konkretes, messbares Ereignis – kein „würde", kein „vielleicht".
                  </p>
                  {isHighImpact && (
                    <p className="text-xs font-semibold text-destructive mt-1.5">
                      {computedImpactLevel === 'existential' ? 'Existenzrelevante' : 'Strategische'} Entscheidung – Kipppunkt ist Pflichtfeld mit Zeitrahmen.
                    </p>
                  )}
                </div>
              </div>

              <Textarea
                value={killerCondition}
                onChange={e => setKillerCondition(e.target.value.slice(0, 200))}
                placeholder={
                  optionSelected
                    ? `z.B. Wenn ${optionSelected} nach 14 Tagen weniger als 2 Pilotkunden-Zusagen hat, ist die These widerlegt.`
                    : "z.B. Wenn nach 14 Tagen weniger als 2 Pilotkunden ein verbindliches Go geben, ist die These widerlegt."
                }
                className="bg-background/50 border-border/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20 text-[15px] min-h-[100px] rounded-xl transition-all"
              />

              {/* Feedback row */}
              <div className="flex items-center justify-between">
                <div className="flex-1 space-y-1">
                  {softWarning && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
                      <p className="text-sm font-semibold text-destructive">{softWarning}</p>
                    </div>
                  )}
                  {!softWarning && killerCondition.length > 10 && !isMeasurable && (
                    <p className="text-xs text-muted-foreground">
                      Tipp: Nutze Zahlen, Zeitangaben oder konkrete Ereignisse.
                    </p>
                  )}
                  {!softWarning && killerCondition.length > 10 && !isRiskAligned && dominantRisk && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20">
                      <Shield className="w-4 h-4 text-destructive flex-shrink-0" />
                      <p className="text-xs font-bold text-destructive">
                        Pflicht: Dein Kipppunkt muss das dominante Risiko adressieren. Weiter ist blockiert.
                      </p>
                    </div>
                  )}
                </div>
                <span className={cn(
                  "text-xs tabular-nums font-medium",
                  killerCondition.length >= 180 ? "text-destructive" : "text-muted-foreground/60"
                )}>
                  {killerCondition.length}/200
                </span>
              </div>

              {/* Quality indicators */}
              {killerCondition.length > 10 && (
                <div className="flex items-center gap-5 pt-1 border-t border-border/30">
                  <QualityDot label="Falsifizierbar" passed={isFalsifiable} />
                  <QualityDot label="Messbar" passed={isMeasurable} />
                  <QualityDot label="Risiko-Bezug" passed={isRiskAligned} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Live Summary */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <LiveCommitSummary
              thesisText={thesis?.thesis_text || ""}
              killerCondition={killerCondition}
              reversibility={getRevLabel(thesis?.reversibility || ctx?.reversibility)}
              optionChosen={thesis?.option_chosen}
              optionRejected={thesis?.option_rejected}
              dominantRisk={dominantRisk}
              optionSelected={optionSelected}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-component ─────────────────────────────────────
const QualityDot = ({ label, passed }: { label: string; passed: boolean }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className={cn(
      "w-2.5 h-2.5 rounded-full transition-all duration-300",
      passed 
        ? "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.4)]" 
        : "bg-muted-foreground/20"
    )} />
    <span className={cn(
      "text-xs font-medium transition-colors",
      passed ? "text-foreground" : "text-muted-foreground/50"
    )}>
      {label}
    </span>
  </div>
);
